import { SalariesBranchExpander } from './branch-expander/salaries-branch-expander';
import React from 'react';
import { SelectedRow } from '../salaries-generator-page';
import {
  PendingPaymentBranch,
  WorkingHours,
} from '../../../../api/types/accountants/salaries-pending-payments/salaries-pending-payments.type';
import { toast } from 'react-toastify';
import { LecturerRateDisplayType } from '../../../../api/types/lecturers/price-rate/lecturer-rate-display-type';
import { TravelExpensesTable } from './travel-expenses-table';

export interface SalaryProfessorPendingPaymentsDatamodel {
  pendingPaymentByBranch: PendingPaymentBranch[];
  selectedRows: SelectedRow[];
  setSelectedRows: (selectedRows: SelectedRow[]) => any;
}

export const SalaryProfessorPendingPayments: React.FC<
  SalaryProfessorPendingPaymentsDatamodel
> = ({ pendingPaymentByBranch, selectedRows, setSelectedRows }) => {
  // Helper function to calculate branch summaries

  function getTotalWorkingHours(workingHours: WorkingHours[]): number {
    return workingHours.reduce((total, hours) => {
      const deltaMs =
        new Date(hours.endTime).getTime() - new Date(hours.startTime).getTime();
      return total + deltaMs / (1000 * 60 * 60);
    }, 0);
  }

  const calculateBranchSummaries = (branch: PendingPaymentBranch) => {
    let totalHours = 0;
    let totalToPay = 0;
    let rateSum = 0;
    let rateCount = 0;

    branch.courses.forEach((course) => {
      totalHours += course.totalHours;

      course.rates.forEach((rate) => {
        if (rate.rate !== undefined) {
          const totalHoursForCourse = getTotalWorkingHours(rate.workingHours);
          totalToPay =
            totalToPay +
            totalHoursForCourse *
              rate.rate *
              (rate.displayType === LecturerRateDisplayType.OneHour
                ? 1
                : 4 / 3);
          rateSum += rate.rate;
          rateCount++;
        }
      });
    });

    const averageRate = rateCount > 0 ? rateSum / rateCount : 0;

    return {
      totalHours: totalHours.toString(),
      averageRate: averageRate.toFixed(0) + '₪',
      totalToPay: totalToPay.toFixed(0) + '₪',
    };
  };

  // Helper function to calculate activity type summaries for first rate
  const calculateActivityTypeSummaries = (branch: PendingPaymentBranch) => {
    if (
      branch.courses.length === 0 ||
      branch.courses.every((course) => course.rates.length === 0)
    ) {
      return {
        activityType: 'אין פעילות',
        totalHours: '0',
        rate: '0₪',
        totalPaid: '0₪',
        totalToPay: '0₪',
      };
    }

    // Find the first rate from the first course that has rates
    const firstCourseWithRates = branch.courses.find(
      (course) => course.rates.length > 0,
    );
    const firstRate = firstCourseWithRates!.rates[0];

    let totalHours = 0;
    let totalToPay = 0;

    // Calculate totals for courses that have this specific rate/activity
    branch.courses.forEach((course) => {
      const matchingRate = course.rates.find(
        (rate) => rate.activityName === firstRate.activityName,
      );
      if (matchingRate && matchingRate.rate !== undefined) {
        totalHours += course.totalHours;
        totalToPay += course.totalHours * matchingRate.rate;
      }
    });

    return {
      activityType: firstRate.activityName,
      totalHours: totalHours.toString(),
      rate: (firstRate.rate || 0).toString() + '₪',
      totalPaid: '0₪', // Assuming no payments made yet
      totalToPay: totalToPay.toFixed(0) + '₪',
    };
  };

  return (
    <div style={{ width: '100%' }}>
      {pendingPaymentByBranch.map((branch, index) => {
        const branchSummaries = calculateBranchSummaries(branch);
        const activitySummaries = calculateActivityTypeSummaries(branch);

        return (
          <SalariesBranchExpander
            key={branch.branchId}
            branchId={branch.branchId.toString()}
            branchName={branch.branchName}
            branchTotalHours={branchSummaries.totalHours}
            branchRate={branchSummaries.averageRate}
            branchTotalToPay={branchSummaries.totalToPay}
            activityType={activitySummaries.activityType}
            activityTotalHours={activitySummaries.totalHours}
            activityRate={activitySummaries.rate}
            activityTotalPaid={activitySummaries.totalPaid}
            activityTotalToPay={activitySummaries.totalToPay}
            pendingPaymentBranch={branch}
          />
        );
      })}
      <TravelExpensesTable pendingPaymentByBranch={pendingPaymentByBranch} />
    </div>
  );
};
